import type { SimAssetAuthorization } from "@lib/simulation/simAssetCredentialStore";

import { omgApiClient } from "./omgApiClient";

/**
 * Media signing endpoints on omg-api, replacing the last two GraphQL fields
 * the app consumed (getCloudfrontCookies / authorizeSimulationAssets). Both
 * callers already hold a token getter, so these take the access token as an
 * argument instead of resolving it themselves.
 */

/**
 * CloudFront signed-cookie values for video playback, as raw `name=value`
 * strings (the values contain `=`; `writeCloudFrontCookies` splits on the
 * first one).
 */
export const getCloudFrontCookies = async (
  accessToken: string | undefined,
): Promise<string[]> => {
  const response = await omgApiClient.get<string[]>(
    "/media/cloudfront-cookies",
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      // The shared client has no default timeout; without one a hung request
      // never settles and react-query never reaches `isError`. Video players
      // wait on this cookie (OMG-2533, `useCloudFrontCookiesReady`), so bound
      // it to the same deadline the gate uses.
      timeout: 8_000,
    },
  );
  return response.data;
};

/**
 * CloudFront query-string credentials covering every asset of one simulation
 * version. The policy is minted per member (requires the `season-1`
 * entitlement server-side, fgsake-monorepo#547).
 */
export const authorizeSimulationAssets = async (
  version: string,
  accessToken: string | undefined,
): Promise<SimAssetAuthorization> => {
  const response = await omgApiClient.get<SimAssetAuthorization>(
    "/media/simulation-assets",
    {
      params: { version },
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return response.data;
};
